import classNames from 'classnames';
import { NextRouter, useRouter } from 'next/router';
import React, {
  FC,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import { isMobile, isSayWeeeApp } from 'utils/common/device';
import Button from '@/components/common/Button';

import Constants from '@/constants/const';
import { twMerge } from 'tailwind-merge';
import { DrawerType } from '@/components/homepage/components/Header/types';
import DropDownStatic, { defaultIconSize } from '@/components/common/EnkiDummyUI/DropDownStatic';
import NavTab from '@/components/common/EnkiDummyUI/NavTab';
import SearchInputStaticMask from '@/components/common/EnkiDummyUI/SearchInputStaticMask';
import ProfileIcon from '@/components/common/EnkiSmartUI/ProfileIcon';
import CartIcon from '@/components/common/EnkiSmartUI/CartIcon';

import { StoreFilled, LocationPinFilled, HamburgerFilled, SearchOutline, HomeFilled } from '@weee-fe/kunlunicon';
import LazySearch from '@/components/common/SearchOverlay/LazySearch';

import { OnClickFunctionType } from '@/types/common-dom/types';
import useTranslation from 'next-translate/useTranslation';
import { size3xsWithTap } from '@/utils/weee-to-enki-tw-mask/buttons';
import { getAddressCity, getZipcode } from '@/utils/common/porder';
import { getCookie } from '@/utils/cookie';
import useGlobalNavHeader from '@/hooks/globalnavheader/provider';
import { GlobalNavHeaderDrawerProvider } from '@/hooks/globalnavheader/GlobalNavHeaderDrawerContext';
import { useMergedGlobalStoreInfo } from '@/hooks/globalnavheader/useMergedGlobalStoreInfo';
import HorizontalSlideList from '../HorizontalSlideList';
import { CuisineCategory, SecondCuisineCategory } from '@/types/category';
import eventemitter, { actions } from '@/utils/eventemitter';
import SearchOverlay, { SearchOverlayProps } from '@/components/common/SearchOverlay';
import { getUrlQuery } from '@/utils/tool';
import useCart from '@/store/shopping';
import useUserInfo from '@/hooks/useUserInfo';
import { useCroppedImage } from '@weee-fe/module';
import { WeeeIcon } from '../Icon';
import IconWithFloatingContent from '../EnkiDummyUI/IconWithFloatingContent';
import CommonDrawer from '@/components/homepage/components/CommonDrawer';
import useHomepage from '@/store/home';

import LOGO from '../../homepage/components/Header/images/latino-logo.svg';
import { isLatinoChannel } from '@/utils/common/latino-app';
import { CategoriesPropItem, getCategoriesList } from '@/api/item';
import LanguageSelect from '@/components/common/language-select';
import NavStoreName from '@/components/homepage/components/CommonDrawer/nav-store-name';

type GlobalNavHeaderPropsType = {
  customFirstLevel?: ReactNode;
  customSecondLevel?: ReactNode;
  customThirdLevel?: ReactNode;
  isSearchMode?: boolean;
  searchTrigger?: (keyword: string, trigger_type?: string) => void;
  searchOverlayProps?: SearchOverlayProps;
  hasDivider?: boolean;
  observedStickyRef?: any;
  categoryRowDynamicScrollRef?: React.MutableRefObject<HTMLElement>;
  showCategoriesPastRandomScroll?: boolean;
  /**
   * Whether to use the search pop-up box
   */
  useSearchModal?: ObjectType;
  showStore?: boolean;
};

enum navList {
  home = '/',
  explore = '/category/sale',
  global = '/mkpl/global',
  waterfall = '/mkpl/waterfall',
  community = '/social',
  account = '/account',
  cart = '/cart'
}

const HAS_MENU = true;
const GLOBAL_PLUS_INSP_INSIDE_MENU = true;

const ES_TARGET_STR_DAY_OF_WEEK_ETA_FIX = 'para el '; // "para el miércoles" (for friday)
const ES_TARGET_STR_LONG_DATE_ETA_FIX = 'para '; // "para mayo 8" (for May 8th)
const PT_TARGET_STR_DAY_OF_WEEK_ETA_FIX = '-feira'; // "Até quinta-feira" (Until Thursday)
const PT_TARGET_STR_LONG_DATE_ETA_FIX = 'até '; // "Até mai 7" (until May 7th)

const loyaltyTierIcons = {
  1: 'https://img06.weeecdn.com/common/rewards/weee_rewards_account_icon_Bronze.png',
  2: 'https://img06.weeecdn.com/common/rewards/weee_rewards_account_icon_Silver.png',
  3: 'https://img06.weeecdn.com/common/rewards/weee_rewards_account_icon_Gold.png'
};

const loyaltyTierIconsMasGusto = {
  1: 'https://img08.weeecdn.net/growth/image/weee_rewards_account_icon_Bronze.png',
  2: 'https://img08.weeecdn.net/growth/image/weee_rewards_account_icon_Silver.png',
  3: 'https://img08.weeecdn.net/growth/image/weee_rewards_account_icon_Gold.png'
};

const MKPTL_SEARCH_ENTRY_ROUTES = {
  /* ['/mkpl/waterfall']: true OLD */
};

const SEARCH_ROUTE_RELATIVE_PATH = '/search';
const MKTPL_SEARCH_ROUTE_RELATIVE_PATH = '/mkpl/search';

/* @globalNavHeaderUnstickedHeight: Tailwind top of the sticky global nav.
Should be 0 if entire GlobalNavHeader scrolls with the page.
Should be NEGATIVE height of first section in case it scrolls away with the page
*/
export const globalNavHeaderUnstickedHeight = 'top-[-30px]';

type PopUpsContainerProps = {
  className?: string;
  categoriesList?: CategoriesPropItem[];
};

export const PopUpsContainer = forwardRef((props: PopUpsContainerProps, ref) => {
  const drawerRef = useRef(null);

  const openDrawerGlobalHeader = (...args) => {
    drawerRef.current.show(...args);
  };

  useImperativeHandle(ref, () => ({
    openDrawerGlobalHeader: openDrawerGlobalHeader
  }));

  return (
    <div className={twMerge('absolute', props?.className)}>
      <CommonDrawer customZIndex={1002} ref={drawerRef} categoriesList={props?.categoriesList} />
    </div>
  );
});

export default function GlobalNavHeader(props: GlobalNavHeaderPropsType) {
  const {
    customFirstLevel,
    customSecondLevel,
    customThirdLevel,
    isSearchMode,
    searchTrigger,
    searchOverlayProps,
    hasDivider,
    observedStickyRef,
    useSearchModal,
    showStore: propsShowStore = true
  } = props;

  const isLatino = isLatinoChannel();

  const [showDeals, setShowDeals] = useState(false);
  const [categoriesList, setCategoriesList] = useState<CategoriesPropItem[]>([]);

  const { t, lang } = useTranslation('common');
  const isLogin: boolean = getCookie(Constants.IS_LOGIN) === '1';
  // const showGlobal: boolean = getIsSupportGlobal();
  /* CAUTION useHomepage: Be aware of GlobalNavHeaderProvider and its wrapping
  around HomepageDrawerCompatibleProvider (A provider of O1 access info
  that every page with GlobaNavHeader needs and historically has been
  constrained to HomepageProvider) - Here we will have a sub-set of homepage data. */
  const { store } = useHomepage();
  const globalNavHeader = useGlobalNavHeader();
  const { loyaltyLevel, userImageUrl } = useUserInfo();
  const { storeInfo } = store || {};
  const zipcode = getZipcode();
  const city = getAddressCity();

  const router = useRouter();
  const query = router?.query as {
    [key: string]: any;
  };

  const searchOverlayRef = useRef(null);
  const popUpControlsRef = useRef(null);
  const defaultRef = useRef(null);
  const categoryRowDynamicScrollRef = props?.categoryRowDynamicScrollRef || defaultRef;
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [overlayVisible, setOverlayVisible] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>(query?.keyword || getUrlQuery('keyword'));
  const { count, simple } = useCart();
  const { showStore, langMayHaveStore } = useMergedGlobalStoreInfo();
  const hasGlobalSupport = globalNavHeader?.hasGlobalSupport ? true : false;
  const currentPath = router.asPath;
  const showCategoriesPastRandomScroll = props?.showCategoriesPastRandomScroll || false;

  // const categoryLinks = globalNavHeader?.categoryLinks;
  const [categoryLinks, setCategoryLinks] = useState<CuisineCategory[]>([]);
  const parentCategory = globalNavHeader?.productParentCategory;
  const productSubCategory = globalNavHeader?.productSubCategory;
  const { getCroppedUrl } = useCroppedImage();
  const supportLanguages = globalNavHeader?.supportLanguages || [];

  const [selectedCategoryLink, setSelectedCategoryLink] = useState<CuisineCategory>();

  useEffect(() => {
    const selectedOne = (categoryLinks || []).find(category => category?.key === parentCategory);
    setSelectedCategoryLink(selectedOne);
  }, [categoryLinks, parentCategory]);

  const handleCategoryItemOnClickCategory = useCallback(({ item, link }) => {
    setSelectedCategoryLink(item);
  }, []);

  useEffect(() => {
    eventemitter.on(actions.categoryItemOnClickCategory, handleCategoryItemOnClickCategory);
    return () => {
      eventemitter.off(actions.categoryItemOnClickCategory, handleCategoryItemOnClickCategory);
    };
  }, [handleCategoryItemOnClickCategory]);

  const zipcodeAndETAStringShortUI = useMemo(() => {
    if (!isLatino) return undefined;
    if (!zipcode || !simple || !simple?.eta_date_desc) return undefined;
    if (lang == Constants.LANGUAGE_ES) {
      const defaultStr = simple?.eta_date_desc;
      let finalStr;
      let tempStr = (defaultStr || '').toLowerCase();
      if (tempStr.startsWith(ES_TARGET_STR_DAY_OF_WEEK_ETA_FIX)) {
        tempStr = defaultStr.substring(ES_TARGET_STR_DAY_OF_WEEK_ETA_FIX.length);
        if (tempStr.startsWith('jueves')) {
          finalStr = 'Jue.';
        } else if (tempStr.startsWith('lunes')) {
          finalStr = 'Lun.';
        } else if (tempStr.startsWith('martes')) {
          finalStr = 'Mar.';
        } else if (tempStr.startsWith('miércoles')) {
          finalStr = 'Mié.';
        } else if (tempStr.startsWith('sábado')) {
          finalStr = 'Sáb.';
        } else if (tempStr.startsWith('domingo')) {
          finalStr = 'Dom.';
        }
      } else if (tempStr.startsWith(ES_TARGET_STR_LONG_DATE_ETA_FIX)) {
        tempStr = defaultStr.substring(ES_TARGET_STR_LONG_DATE_ETA_FIX.length);
        if (tempStr.startsWith('enero ')) {
          finalStr = tempStr.replace('enero ', 'Ene. ');
        } else if (tempStr.startsWith('febrero ')) {
          finalStr = tempStr.replace('febrero ', 'Feb. ');
        } else if (tempStr.startsWith('marzo ')) {
          finalStr = tempStr.replace('marzo ', 'Mar. ');
        } else if (tempStr.startsWith('abril ')) {
          finalStr = tempStr.replace('abril ', 'Abr. ');
        } else if (tempStr.startsWith('mayo ')) {
          finalStr = tempStr.replace('mayo ', 'May. ');
        } else if (tempStr.startsWith('junio ')) {
          finalStr = tempStr.replace('junio ', 'Jun. ');
        } else if (tempStr.startsWith('julio ')) {
          finalStr = tempStr.replace('julio ', 'Jul. ');
        } else if (tempStr.startsWith('agosto ')) {
          finalStr = tempStr.replace('agosto ', 'Ago. ');
        } else if (tempStr.startsWith('septiembre ')) {
          finalStr = tempStr.replace('septiembre ', 'Sep. ');
        } else if (tempStr.startsWith('octubre ')) {
          finalStr = tempStr.replace('octubre ', 'Oct. ');
        } else if (tempStr.startsWith('noviembre ')) {
          finalStr = tempStr.replace('noviembre ', 'Nov. ');
        } else if (tempStr.startsWith('diciembre ')) {
          finalStr = tempStr.replace('diciembre ', 'Dic. ');
        }
      }
      return finalStr || defaultStr;
    } else if (lang == Constants.LANGUAGE_PT) {
      const defaultStr = simple?.eta_date_desc;
      let finalStr;
      let tempStr = (defaultStr || '').toLowerCase();
      if (tempStr.endsWith(PT_TARGET_STR_DAY_OF_WEEK_ETA_FIX)) {
        finalStr = defaultStr.substring(0, defaultStr.length - PT_TARGET_STR_DAY_OF_WEEK_ETA_FIX.length);
      } else if (tempStr.startsWith(PT_TARGET_STR_LONG_DATE_ETA_FIX)) {
        if (tempStr.indexOf(' jan ') > -1) {
          finalStr = defaultStr.replace(' jan ', ' Jan. ');
        } else if (defaultStr.indexOf(' fev ') > -1) {
          finalStr = defaultStr.replace('fev ', ' Fev. ');
        } else if (defaultStr.indexOf(' mar ') > -1) {
          finalStr = defaultStr.replace(' mar ', ' Mar. ');
        } else if (defaultStr.indexOf(' abr ') > -1) {
          finalStr = defaultStr.replace(' abr ', ' Abr. ');
        } else if (defaultStr.indexOf(' mai ') > -1) {
          finalStr = defaultStr.replace(' mai ', ' Maio ');
        } else if (defaultStr.indexOf(' jun ') > -1) {
          finalStr = defaultStr.replace(' jun ', ' Jun. ');
        } else if (defaultStr.indexOf(' jul ') > -1) {
          finalStr = defaultStr.replace(' jul ', ' Jul. ');
        } else if (defaultStr.indexOf(' ago ') > -1) {
          finalStr = defaultStr.replace(' agosto ', ' Ago. ');
        } else if (defaultStr.indexOf(' set ') > -1) {
          finalStr = defaultStr.replace(' set ', ' Set. ');
        } else if (defaultStr.indexOf(' out ') > -1) {
          finalStr = defaultStr.replace(' out ', ' Out. ');
        } else if (defaultStr.indexOf(' nov ') > -1) {
          finalStr = defaultStr.replace(' nov ', ' Nov. ');
        } else if (defaultStr.indexOf(' dez ') > -1) {
          finalStr = defaultStr.replace(' dez ', ' Dez. ');
        } else if (defaultStr.indexOf(' sábado') > -1) {
          finalStr = defaultStr.replace(' sábado', ' Sáb.'); // Saturday
        } else if (defaultStr.indexOf(' domingo') > -1) {
          finalStr = defaultStr.replace(' domingo', ' Dom.'); // Sunday
        }
      }
      return finalStr || defaultStr;
    }

    return undefined;
  }, [zipcode, simple, lang]);

  const handleMenuToggle = useCallback(e => {
    setIsMenuOpen(isMenuOpen => !isMenuOpen);
  }, []);

  const onCategoryClickCb = useCallback(
    event => {
      if (showCategoriesPastRandomScroll === true) {
        window.scrollTo(0, 0);
      }
    },
    [showCategoriesPastRandomScroll]
  );

  const handleClick = (e, routeLink) => {
    handleMenuToggle(e);
    router.push(routeLink);
  };

  const openGlobalNavHeaderDrawer = useCallback((type: DrawerType, isClosable?: boolean, options?: ObjectType) => {
    popUpControlsRef.current?.openDrawerGlobalHeader?.(type, isClosable, options);
  }, []);

  const onZipcodeClick: OnClickFunctionType = useCallback(() => {
    openGlobalNavHeaderDrawer(simple?.is_support_change_date ? DrawerType.delivery : DrawerType.zipcode);
  }, [simple?.is_support_change_date, openGlobalNavHeaderDrawer]);

  const onChangeAddressClick: OnClickFunctionType = useCallback(() => {
    openGlobalNavHeaderDrawer(DrawerType.zipcode);
  }, [openGlobalNavHeaderDrawer]);

  const onChangeDeliveryDateClick: OnClickFunctionType = useCallback(() => {
    openGlobalNavHeaderDrawer(DrawerType.date);
  }, [openGlobalNavHeaderDrawer]);

  const onHamburgerClick: OnClickFunctionType = useCallback(() => {
    openGlobalNavHeaderDrawer(DrawerType.hamburgerMenu);
  }, [openGlobalNavHeaderDrawer]);

  const globalNavHeaderDrawerContextValue = useMemo(() => ({ openGlobalNavHeaderDrawer }), [openGlobalNavHeaderDrawer]);

  const handleSearchNavigationClick: OnClickFunctionType = useCallback(
    e => {
      if (isSearchMode) {
        return null;
      }

      let destination = SEARCH_ROUTE_RELATIVE_PATH;
      if (MKPTL_SEARCH_ENTRY_ROUTES[router?.asPath] === true) {
        destination = MKTPL_SEARCH_ROUTE_RELATIVE_PATH;
      }

      const customSearchQuery = searchOverlayProps?.customSearchQuery;
      if (customSearchQuery && typeof customSearchQuery === 'object') {
        const sp = new URLSearchParams();
        Object.entries(customSearchQuery).forEach(([k, v]) => {
          if (k && v != null && v !== '') sp.set(k, String(v));
        });
        if (sp.toString()) {
          destination += (destination.includes('?') ? '&' : '?') + sp.toString();
        }
      }

      if (isSayWeeeApp()) {
        return (window.location.href = destination);
      }
      if (!isMobile()) {
        return (window.location.href = destination);
      }
      router.push(destination);
    },
    [isSearchMode, searchOverlayProps?.customSearchQuery]
  );

  const handleTabClick = useCallback(
    (link, e) => {
      if (link === navList.home) {
        e.preventDefault();
        eventemitter.emit(actions.blockRoutePushScrollRestoration, navList.home);
      }
      if (link === navList.community) {
        window.location.href = link; // Another repo
        return null;
      }
      router.push({
        pathname: link
      });
    },
    [router.asPath]
  );

  const handleExploreTabClick = useCallback(
    (link, event) => {
      if (event?.stopPropagation) event.stopPropagation();
      if (event?.preventDefault) event.preventDefault();
      router.push(link);
      eventemitter.emit(actions.globalNavHeaderOnClickCategory, { item: { key: 'sale', num: 'sale' }, link: link });
    },
    [router.asPath, router.query]
  );

  const handleUseAppClick: OnClickFunctionType = useCallback(e => {
    const storeKey = getCookie(Constants.WEEE_STORE);
    const link = 'https://www.masgusto.com/company/download-masgusto';
    let downloadUrl = link;
    if (lang !== Constants.LANGUAGE_EN) {
      if (isLatino) {
        downloadUrl = `${link}-${lang === Constants.LANGUAGE_PT ? 'pt-br' : storeKey}`;
      } else {
        downloadUrl = `${link.replace('mgp', 'company')}-${lang}`;
      }
    }

    window.location.href = downloadUrl;
  }, []);

  const handleLoginClick: OnClickFunctionType = useCallback(e => {
    router.push('/account/login');
  }, []);

  const onSearchChange = useCallback(
    e => {
      if (searchOverlayRef?.current?.handleInputKeyword) {
        searchOverlayRef.current.handleInputKeyword(e);
      }
    },
    [router.asPath, router.query, isSearchMode]
  );

  const onSearchFocus = useCallback(
    e => {
      if (searchOverlayRef?.current?.handleInputFocus) {
        searchOverlayRef.current.handleInputFocus(e);
      }
    },
    [router.asPath, router.query, isSearchMode]
  );

  const onSearchKeyDown = useCallback(
    e => {
      if (searchOverlayRef?.current?.handleInputEnter) {
        searchOverlayRef.current.handleInputEnter(e);
      }
    },
    [router.asPath, router.query, isSearchMode]
  );

  const onSearchClearInput = useCallback(
    e => {
      if (searchOverlayRef?.current?.handleClearKeyword) {
        searchOverlayRef.current.handleClearKeyword(e);
      }
    },
    [router.asPath, router.query, isSearchMode]
  );

  const handleSearchCancel = useCallback(
    e => {
      if (searchOverlayRef?.current?.handleCancel) {
        searchOverlayRef.current.handleCancel(e);
      }
    },
    [router.asPath, router.query, isSearchMode]
  );

  const onSearchOverlayVisibilityChange = useCallback(
    isVisible => {
      setOverlayVisible(isVisible);
    },
    [router.asPath, router.query, isSearchMode]
  );

  const onSearchOverlayKeywordChange = useCallback(
    keyword => {
      setKeyword(keyword);
    },
    [router.asPath, router.query, isSearchMode]
  );

  useEffect(() => {
    // 获取是否显示菜单接口
    getCategoriesList({ type: 'special' }).then(res => {
      if (res.result) {
        const list = res?.object?.category_list || [];
        setCategoriesList(list);
        setShowDeals(list.some(item => item.num === 'sale'));
      }
    });
    // 获取显示的菜单目录接口
    getCategoriesList({ type: 'home_normal' }).then(res => {
      if (res.result) {
        const list = ((res?.object?.category_list as unknown) as CuisineCategory[]) || [];
        setCategoryLinks(list);
      }
    });
  }, []);

  const renderFirstLevel = () => {
    if (isSearchMode && overlayVisible) return null;

    const __renderZipAndETA = () => {
      let label;
      if (zipcode && simple?.eta_date_desc) {
        const shortLabel = zipcodeAndETAStringShortUI != undefined ? zipcodeAndETAStringShortUI : simple?.eta_date_desc;
        label = (
          <span className="flex gap-x-[4px] items-center enki-body-3xs-medium">
            {zipcode}
            <span className="flex-none">•</span>
            {shortLabel}
          </span>
        );
      } else if (zipcode) {
        label = <span className="flex gap-x-[6px] enki-body-3xs-medium">{zipcode}</span>;
      } else {
        label = (
          <span className="flex gap-x-[6px]">
            <span className="inline-flex skeleton bg-surface-200-bg w-[40px] h-[14px] rounded-full self-center"></span>
            <span className="inline-flex skeleton bg-surface-200-bg w-[71px] h-[14px] rounded-full self-center"></span>
          </span>
        );
      }
      return (
        <DropDownStatic
          onClick={onZipcodeClick}
          mainIcon={zipcode && <LocationPinFilled width={11} height={11} className="text-link-base-1 mb-[2px]" />}
          dataTestId="wid-modal-zip-code-and-eta"
          label={label}
          mainColorTwNotation={'text-link-base-1 enki-body-3xs-medium'}
          wrapperClassName="justify-center px-1.5"
          hideCarat={true}
        />
      );
    };

    const __renderCityZipChangeAddress = () => {
      let label;
      if (zipcode) {
        label = zipcode;
      } else {
        label = (
          <span className="inline-flex skeleton bg-surface-200-bg w-[70px] h-[14px] rounded-full self-center"></span>
        );
      }
      return (
        <DropDownStatic
          onClick={onChangeAddressClick}
          mainIcon={
            zipcode && (
              <span>
                <LocationPinFilled width={11} height={11} className="text-link-base-1 -mb-[1px]" />
              </span>
            )
          }
          dataTestId="wid-modal-city-zipcode-and-change-address"
          label={label}
          mainColorTwNotation={'text-link-base-1 enki-body-3xs-medium'}
          hideCarat={true}
          wrapperClassName="justify-center"
        />
      );
    };

    const __renderETAChangeDelivery = () => {
      let label =
        isLatino && zipcodeAndETAStringShortUI != undefined && !showStore
          ? zipcodeAndETAStringShortUI
          : simple?.eta_date_desc;
      if (lang === Constants.LANGUAGE_VI) {
        if (label?.startsWith?.('Giao vào ')) {
          label = label.substring('Giao vào '.length);
        }
      }
      return (
        <DropDownStatic
          onClick={onChangeDeliveryDateClick}
          dataTestId="wid-modal-eta-and-change-delivery"
          mainIcon={zipcode ? <span className="flex-none text-link-base-1">•</span> : null}
          label={label}
          mainColorTwNotation={'text-link-base-1 enki-body-3xs-medium'}
          hideCarat={true}
          wrapperClassName="justify-center"
        />
      );
    };

    const leftNode = (
      <>
        {propsShowStore && <NavStoreName />}
        <LanguageSelect />
      </>
    );

    const rightNode = (
      <>
        {langMayHaveStore && showStore && __renderZipAndETA()}
        {(!langMayHaveStore || !showStore) && __renderCityZipChangeAddress()}
        {!showStore && simple?.is_support_change_date && __renderETAChangeDelivery()}
      </>
    );

    return (
      <div className="flex items-center justify-between px-3 py-100 gap-50">
        <div className="flex gap-[6px] items-center">{leftNode}</div>
        <div className="flex gap-[6px] items-center">{rightNode}</div>
      </div>
    );
  };

  const renderSecondLevel = () => {
    let leftNode;
    if (isSearchMode && overlayVisible) {
      leftNode = (
        <IconWithFloatingContent
          ariaLabel={t('generic_back_nav_short')}
          className="w-[29px] h-[29px] ml-[-8px] flex items-center justify-center [&_.icon]:text-[32px] text-primary-1"
          icon={<WeeeIcon type="iconBack" className="" aria-hidden />}
          floatingContent={null}
          onClick={handleSearchCancel}
        />
      );
    } else {
      leftNode = (
        <>
          <IconWithFloatingContent
            ariaLabel={t('tabbar_item_menu')}
            wrapperClassName="ml-[-12px] mt-[2px]"
            icon={<HamburgerFilled width={24} height={24} className="text-primary-1 pr-[1px]" aria-hidden />}
            onClick={onHamburgerClick}
            dataTestId="btn-hamburger-menu"
          />
          <a data-testid="wid-weee-logo" className="flex" href="/" onClick={handleTabClick.bind(this, navList.home)}>
            <img
              className={classNames({ 'w-[107px] h-[19px]': isLatino, 'w-[69px] h-[20px]': !isLatino })}
              src={LOGO}
              alt="logo"
            />
          </a>
        </>
      );
    }

    const loyaltyIconsMap = isLatino ? loyaltyTierIconsMasGusto : loyaltyTierIcons;

    const rightNode = (
      <>
        {!!useSearchModal ? (
          <LazySearch {...useSearchModal} moduleName="global">
            <div className="flex items-center px-200 h-[30px] box-border w-full rounded-full bg-surface-200-bg">
              <div className="flex items-center p-100">
                <SearchOutline width={12} height={12} className="text-surface-100-fg-minor" />
              </div>
              <span className="enki-body-xs text-input-100-fg-placeholder block max-w-[120px] overflow-hidden whitespace-nowrap leading-[1.6rem]">
                {t('search_placeholder')}
              </span>
            </div>
          </LazySearch>
        ) : (
          <SearchInputStaticMask
            keyword={keyword}
            onClick={isSearchMode ? null : handleSearchNavigationClick}
            clickSetsSelectionRange={!!isSearchMode && !!keyword}
            inputAutoFocus={isSearchMode && !keyword}
            handleChange={onSearchChange}
            handleFocus={onSearchFocus}
            handleKeyDown={onSearchKeyDown}
            handleClearKeyword={onSearchClearInput}
            placeholder={isSearchMode && overlayVisible ? t('common_search_placeholder') : undefined}
          />
        )}

        {(!isSearchMode || (isSearchMode && !overlayVisible)) && (
          <div>
            {isLogin ? (
              <ProfileIcon
                altText={'Profile'}
                onClick={handleTabClick.bind(this, navList.account)}
                userAvatar={userImageUrl}
                dataTestId="wid-profile"
                counterNode={
                  isLogin &&
                  Boolean(loyaltyLevel) && (
                    <div
                      className="pointer-events-none absolute -top-[5px] -right-[5px] rounded-full border-solid border-[2px] border-white w-[17px] h-[17px] flex items-center justify-center bg-center bg-cover"
                      style={{
                        backgroundImage: `url(${getCroppedUrl(loyaltyIconsMap[loyaltyLevel] || '', {
                          renderWidth: 34
                        })}`
                      }}
                    />
                  )
                }
              />
            ) : (
              <Button
                className="bg-primary-2 text-reserved-true-white enki-body-3xs-strong min-w-[58px] max-w-[80px] px-2"
                onClick={handleLoginClick}
                dataTestId="btn-signup"
                size="xs"
                type={'secondary'}>
                {t('signup')}
              </Button>
            )}
          </div>
        )}

        <CartIcon
          altText={t('go_to_cart') || 'cart'}
          onClick={handleTabClick.bind(this, navList.cart)}
          dataTestId="wid-cart"
          counterNode={
            !!count &&
            count > 0 && (
              <span
                className={
                  'pointer-events-none absolute box-border -top-[3px] -right-[5px] h-[17px] min-w-[21px] p-[3px] flex items-center justify-center text-reserved-true-white rounded-full bg-pricing-bg enki-body-3xs-strong'
                }>
                {count > 99 ? '99+' : count}
              </span>
            )
          }
        />
      </>
    );

    return (
      <div
        className={twMerge(
          'flex items-center justify-between pl-400 pr-300 py-200 z-2',
          isSearchMode && overlayVisible ? 'gap-100' : 'gap-200'
        )}>
        <span className="flex gap-[8px] items-center">{leftNode}</span>
        <span className="flex gap-100 items-center w-full">{rightNode}</span>
      </div>
    );
  };
  const customTab = (label: string, link: string, isSelected: boolean, dataTestId?: string) => (
    <NavTab label={label} isSelected={isSelected} dataTestId={dataTestId} onClick={handleTabClick.bind(this, link)} />
  );

  const __renderHomeFixedTab = (): ReactNode => {
    const isHomeSelected = currentPath == navList.home;
    return (
      <NavTab
        isSelected={isHomeSelected}
        label={t('tabbar_item_home')}
        onClick={handleTabClick.bind(this, navList.home)}
        dataTestId="wid-nav-tab-home"
        wrapperClassName="pr-100 mr-100 ml-300"
        mainIcon={
          <HomeFilled
            width={14}
            height={14}
            className={twMerge(isHomeSelected ? 'text-primary-1' : 'text-surface-100-fg-minor', 'pr-[1px]')}
          />
        }
      />
    );
  };

  const __renderFixedTabs = (): ReactNode => {
    if (!HAS_MENU || !GLOBAL_PLUS_INSP_INSIDE_MENU) {
      return (
        <>
          {hasGlobalSupport &&
            customTab(
              t('tabbar_item_global'),
              navList.waterfall,
              currentPath == navList.waterfall,
              'wid-nav-tab-global'
            )}
          {customTab(
            t('tabbar_item_community'),
            navList.community,
            currentPath == navList.community,
            'wid-nav-tab-community'
          )}
        </>
      );
    } else {
      return (
        <>
          {showDeals && (
            <NavTab
              wrapperClassName="pl-[6px]"
              isSelected={currentPath == navList.explore}
              label={t('tabbar_item_deals')}
              dataTestId="wid-nav-tab-deals"
              onClick={handleExploreTabClick.bind(this, navList.explore)}
            />
          )}
        </>
      );
    }
  };

  const __renderCategoryLinks = (links: CuisineCategory[]): ReactNode => {
    const buildPathName = (item, isMainCategory) =>
      `/category/${item?.key || item?.catalogue_num}${item?.slug ? `/${item?.slug}` : ''}`;

    return (
      <HorizontalSlideList
        stickyL2Only={false}
        mainCategory={selectedCategoryLink || undefined}
        mainCategoryList={links}
        optimizeForGlobalHeader={true}
        secondCategory={{ catalogue_num: undefined } as SecondCuisineCategory}
        secondCategoryList={[]}
        bringSelectedAsFirstIfNeeded={true}
        onCategoryClick={onCategoryClickCb}
        handleMainCategory={(): void => {}}
        handleSecondCategory={(): void => {}}
        showMainCategorySkeleton={true}
        showShadow={true}
        loading={false}
        changeCategory={false}
        secondLoading={false}
        filterCount={undefined}
        listRef={undefined}
        buildPathName={buildPathName}
        showBadgeOnFilter={true}
        trackCtx={{}}
        sortFilterProps={{}}
        outFilters={undefined}
        selectedOutFilters={undefined}
      />
    );
  };

  const renderThirdLevel = () => {
    const refObj = showCategoriesPastRandomScroll ? { ref: categoryRowDynamicScrollRef } : {};
    const fancyDynamicStyle = !showCategoriesPastRandomScroll
      ? {}
      : {
          opacity: '0',
          pointerEvents: 'none'
        };
    return (
      <div
        {...refObj}
        className={twMerge(
          'flex justify-between flex-row items-center self-stretch min-h-[34px]',
          isSearchMode && overlayVisible ? '!opacity-0' : '',
          !showCategoriesPastRandomScroll
            ? ''
            : '!absolute w-full top-[100%] bg-white transition-[opacity_0.2s_cubic-bezier]'
        )}
        style={fancyDynamicStyle as React.CSSProperties}>
        <div className="inline-flex relative overflow-y-visible shrink-0">
          {__renderHomeFixedTab()}
          <span className="absolute pointer-events-none right-0 w-[1px] bg-tint-black-25 mt-[4px] mb-[4px] bottom-[4px] top-0"></span>
          <span className="absolute pointer-events-none left-[100%] z-[4] w-[19px] h-full bg-[linear-gradient(90deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_100%)]"></span>
        </div>
        <div
          className="flex items-stretch overflow-y-scroll w-full pl-100 relative gap-100"
          id={`${Constants.NAV_CATEGORY_SCROLL_CONTAINER_ID}`}>
          {__renderFixedTabs()}
          {!!categoryLinks?.length && __renderCategoryLinks(categoryLinks)}
        </div>
      </div>
    );
  };

  const renderDivider = () => {
    const __showDivider = isSearchMode || showCategoriesPastRandomScroll ? false : hasDivider;
    return (
      <div
        className={twMerge(
          'w-full h-[1px] mt-0',
          __showDivider ? 'bg-surface-100-hairline' : 'bg-surface-100-bg'
        )}></div>
    );
  };

  const renderSearchMode = () => {
    if (!isSearchMode || !searchOverlayProps) return null;

    return (
      <div
        className={twMerge(
          'bg-white w-full mt-[0px] z-50 absolute top-[100%] h-auto min-h-[100px])',
          classNames({
            'opacity-0': !overlayVisible,
            'pointer-events-none': !overlayVisible
          })
        )}>
        <SearchOverlay
          ref={searchOverlayRef}
          {...searchOverlayProps}
          streamlinedHeader={true}
          onSearchOverlayVisibilityChange={onSearchOverlayVisibilityChange}
          onSearchOverlayKeywordChange={onSearchOverlayKeywordChange}
        />
      </div>
    );
  };

  // console.log('wocao02', categoriesList);

  return (
    <GlobalNavHeaderDrawerProvider value={globalNavHeaderDrawerContextValue}>
      <React.Fragment>
        <PopUpsContainer ref={popUpControlsRef} categoriesList={categoriesList} />
        <nav className="relative bg-white pt-100 flex flex-col text-xs" ref={observedStickyRef}>
          {customFirstLevel ? customFirstLevel : renderFirstLevel()}

          {customSecondLevel ? customSecondLevel : renderSecondLevel()}

          {customThirdLevel ? customThirdLevel : !isSearchMode && renderThirdLevel()}
          {renderDivider()}
          {renderSearchMode()}
        </nav>
      </React.Fragment>
    </GlobalNavHeaderDrawerProvider>
  );
}
